import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { createUseStyles } from 'react-jss';

import usePixel from 'app/hooks/usePixel';

import * as Modal from 'app/components/common/modal/Modal';

import MediaPlayer from 'app/lib/MediaPlayer';
import { useAppContext } from 'app/lib/AppContext';
import { useDispatcher, useSelector } from 'app/store/utils/redux/hooks';
import { IPageGift } from 'app/store/nodes/card/interfaces';

import { ReactComponent as CloseIcon } from './assets/close.svg';

interface IGiftError {}

const GiftUseLater: FC<IGiftError> = (props) => {
  const { bowser } = useAppContext();
  const classes = useStyles();
  const actions = useDispatcher();

  const isIos = bowser.os.name?.toLowerCase() === 'ios';
  const isSafari = bowser.browser.name?.toLowerCase() === 'safari';
  const isAndroid = bowser.os.name?.toLowerCase() === 'android';
  const isOpen = useSelector((state) => state.gift.isOpenUseLater);

  const pixel = usePixel();

  const page = useSelector((state) => {
    return state?.card?.pages[state?.flipbook?.currentPage] as IPageGift;
  });

  useEffect(() => {
    if (isOpen) {
      pixel.enable('https://view.adjust.com/impression/1n0g08q?fallback_lp=https%3A%2F%2Fwww.cardsnacks.com');
    }
  }, [pixel, isOpen]);

  const handleClose = useCallback(() => {
    actions.gift.setOpenUseLater(false);
  }, [actions]);

  const handleDownloadApp = useCallback(() => {
    if (isIos && isSafari) {
      MediaPlayer.muteAll('deep-link');
      setTimeout(() => {
        MediaPlayer.unmuteAll('deep-link');
      }, 3000);
    }
    window.open('https://xnzu.adj.st/?adj_t=1n0g08q&adj_redirect_macos=https%3A%2F%2Fwww.cardsnacks.com', '_blank');
  }, [isIos, isSafari]);

  const renderButton = useMemo(() => {
    if (isIos) {
      return <button type="button" className={classes.buttonDownloadApp} onClick={handleDownloadApp}>Download App</button>;
    }
    if (isAndroid) {
      return <a className={classes.buttonDownloadApp} href="https://cardsnacks.go.link/lBq3x" target="_top">Download App</a>;
    }
    return <a className={classes.buttonDownloadApp} href="https://cardsnacks.go.link/lBq3x" target="_blank" rel="noreferrer">Download App</a>;
  }, [isIos, isAndroid, handleDownloadApp]);

  if (page?.type !== 'PageGift') {
    return null;
  }

  return (
    <Modal.Item
      visible={isOpen}
      hAlign="center"
      vAlign="flex-start"
      onOutsideClick={handleClose}
    >
      <div className={classes.GiftUseLater}>
        <div className={classes.body}>
          <button type="button" className={classes.buttonClose} onClick={handleClose}>
            <CloseIcon fill="#3F3F3F" />
          </button>
          <div className={classes.title}>
            Find your
            <br />
            gift card easily
          </div>
          <div className={classes.description}>
            Get the CardSnacks App
            <br />
            (it&apos;s free)
          </div>
          <div className={classes.buttons}>
            {renderButton}
          </div>
        </div>
      </div>
    </Modal.Item>
  );
};

const useStyles = createUseStyles({
  GiftUseLater: {
    position: 'relative',
    marginTop: '18rem',
    borderRadius: '1.3rem',
    maxWidth: '30rem',
    background: '#fff',
    boxShadow: '0 0 5.2rem rgba(0,0,0,.35)',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    padding: '2.4rem 3.7rem 3.2rem',
  },
  title: {
    fontFamily: 'Avenir Next Demi,sans-serif',
    fontWeight: '700',
    fontSize: '2.2rem',
    lineHeight: 1.3,
    textAlign: 'center',
    color: '#454545',
  },
  description: {
    fontSize: '1.6rem',
    lineHeight: 1.3,
    textAlign: 'center',
    marginTop: '1.2rem',
    color: '#454545',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '3.2rem',
  },
  buttonClose: {
    position: 'absolute',
    display: 'flex',
    top: '1.2rem',
    right: '1.2rem',
    width: '2.4rem',
    height: '2.4rem',
    border: 0,
    background: 'none',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
  },
  buttonDownloadApp: {
    display: 'flex',
    color: '#3F3F3F',
    background: 'transparent',
    fontSize: '1.5rem',
    fontWeight: '600',
    lineHeight: '4.6rem',
    border: '1px solid #9B9B9B',
    borderRadius: '4.8rem',
    justifyContent: 'center',
    outline: 'none',
    cursor: 'pointer',
    alignItems: 'center',
    padding: '0 3rem',
    whiteSpace: 'nowrap',
  },
  buttonOs: {
    '&': {
      minWidth: '17rem',
      lineHeight: 1.3,
      alignItems: 'center',
      height: '4.6rem',
      marginTop: '2.4rem',
      padding: '0 2rem',
    },
    '&:first-child': {
      marginTop: 0,
    },
  },
  icon: {
    width: '2.4rem',
    height: '2.4rem',
    marginRight: '0.5rem',
  },
  text: {
  },
  link: {
    position: 'absolute',
    visibility: 'hidden',
    top: 0,
    left: 0,
  },
});

export default GiftUseLater;
